import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLandingData } from "../../../hooks/useLandingData";

export default function FAQSection() {
  const { t } = useTranslation();
  const { faqItems } = useLandingData();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [openId, setOpenId] = useState<string | null>("f1");
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { key: "all", label: t("faq_cat_all") },
    { key: "Technology", labelEs: "Tecnología", label: t("faq_cat_technology") },
    { key: "Privacy", labelEs: "Privacidad", label: t("faq_cat_privacy") },
    { key: "Setup", labelEs: "Configuración", label: t("faq_cat_config") },
    { key: "Business", labelEs: "Negocio", label: t("faq_cat_business") },
    { key: "Compatibility", labelEs: "Compatibilidad", label: t("faq_cat_compatibility") },
    { key: "Implementation", labelEs: "Implementación", label: t("faq_cat_implementation") },
    { key: "Integrations", labelEs: "Integraciones", label: t("faq_cat_integrations") },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // faqItems category field can be in either language; match by english key or spanish equivalent
  const filtered = activeCategory === "all"
    ? faqItems
    : faqItems.filter((f) => {
        const cat = categories.find((c) => c.key === activeCategory);
        if (!cat) return true;
        return f.category === cat.key || f.category === cat.labelEs;
      });

  const toggle = (id: string) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <section ref={ref} className="py-28 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-amber-600 mb-4">
            {t("faq_label")}
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-stone-900 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("faq_title_1")}
            <br />
            <span className="text-amber-500">{t("faq_title_2")}</span>
          </h2>
          <p className="mt-4 text-stone-500 text-lg max-w-xl mx-auto">
            {t("faq_subtitle")}
          </p>
        </div>

        <div
          className={`flex flex-wrap gap-2 justify-center mb-10 transition-all duration-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {categories.map((cat) => (
            <button
              key={cat.key}
              onClick={() => {
                setActiveCategory(cat.key);
                setOpenId(null);
              }}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                activeCategory === cat.key
                  ? "bg-stone-900 text-white"
                  : "bg-stone-100 text-stone-500 hover:bg-stone-200 hover:text-stone-700"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.map((item, i) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                className={`border rounded-2xl overflow-hidden transition-all duration-500 ${
                  isOpen ? "border-amber-200 bg-amber-50/40" : "border-stone-100 bg-white"
                } ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <button
                  onClick={() => toggle(item.id)}
                  className="w-full text-left px-6 py-5 flex items-start gap-4 cursor-pointer group"
                >
                  <div
                    className={`w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full border transition-all duration-300 mt-0.5 ${
                      isOpen
                        ? "bg-amber-500 border-amber-500 text-white rotate-45"
                        : "bg-stone-100 border-stone-200 text-stone-500 group-hover:border-stone-300"
                    }`}
                  >
                    <i className="ri-add-line text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span
                        className={`text-xs font-semibold px-2 py-0.5 rounded-full transition-colors duration-200 ${
                          isOpen ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-400"
                        }`}
                      >
                        {item.category}
                      </span>
                    </div>
                    <p className={`font-semibold text-sm leading-snug transition-colors duration-200 ${isOpen ? "text-stone-900" : "text-stone-700 group-hover:text-stone-900"}`}>
                      {item.question}
                    </p>
                  </div>
                </button>

                <div
                  className="overflow-hidden transition-all duration-400"
                  style={{ maxHeight: isOpen ? "500px" : "0px", opacity: isOpen ? 1 : 0 }}
                >
                  <div className="px-6 pb-6 pl-[68px]">
                    <p className="text-stone-600 text-sm leading-relaxed">{item.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div
          className={`mt-12 rounded-3xl p-8 text-center border border-stone-100 bg-stone-50 transition-all duration-700 delay-500 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-stone-900 mx-auto mb-4">
            <i className="ri-customer-service-2-line text-amber-400 text-xl" />
          </div>
          <h3 className="font-bold text-stone-900 text-lg mb-2">
            {t("faq_unanswered_title")}
          </h3>
          <p className="text-stone-500 text-sm mb-5 max-w-sm mx-auto">
            {t("faq_unanswered_desc")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="mailto:soporteauria@gmail.com"
              rel="nofollow"
              className="flex items-center gap-2 px-5 py-2.5 bg-stone-900 hover:bg-stone-800 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-send-line" /> {t("faq_contact_cta")}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
